'use client'

import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'

import { HeroCycler } from '@/components/hero-cycler'
import { TopoFlow, type TopoFlowHandle } from '@/components/topo-flow'

const HALLOWEEN_ARTWORK = '/halloween/lettering/brand/happy-halloween.webp'

/** Each pose gives the fix line a different resting spot, tilt, and colours.
 *  Four distinct keepers, ordered to maximise contrast between consecutive cycles. */
const FIX_THAT_POSES = [
  // 1. White on purple, clean bright pop
  { colour: '#ffffff', highlight: 'var(--brand-purple)', x: -3, y: 1, r: -1.8 },
  // 2. Chartreuse on purple-dark, neon green on deep plum
  {
    colour: 'var(--brand-chartreuse-vivid)',
    highlight: 'var(--brand-purple-dark)',
    x: -2,
    y: 2,
    r: -0.9,
  },
  // 3. Lavender on forest green
  { colour: 'var(--brand-lavender)', highlight: 'var(--brand-green)', x: 5, y: -2, r: 2.0 },
  // 4. Lavender on chartreuse, bright counterpoint
  {
    colour: 'var(--brand-lavender)',
    highlight: 'var(--brand-chartreuse-vivid)',
    x: 6,
    y: 2,
    r: 1.5,
  },
]

interface HeroInteractiveProps {
  phrases: string[]
  fixLines: string[]
  halloweenPhrase: string
  halloweenFixLine: string
  /** Static content rendered below the headline (CTA buttons, insurance copy) */
  children: ReactNode
}

export function HeroInteractive({
  phrases,
  fixLines,
  halloweenPhrase,
  halloweenFixLine,
  children,
}: HeroInteractiveProps) {
  const allPhrases = useMemo(() => [...phrases, halloweenPhrase], [phrases, halloweenPhrase])
  const artwork = useMemo(() => ({ [halloweenPhrase]: HALLOWEEN_ARTWORK }), [halloweenPhrase])
  const topoRef = useRef<TopoFlowHandle>(null)
  const [poseIndex, setPoseIndex] = useState(0)
  const [halloweenGreeting, setHalloweenGreeting] = useState(false)
  const [pop, setPop] = useState(false)
  const [fixFading, setFixFading] = useState(false)
  const pendingIndex = useRef(0)
  const cycles = useRef(0)

  const handleCycle = useCallback(
    (index: number) => {
      // Fade out the fix line before swapping text
      cycles.current += 1
      pendingIndex.current = cycles.current % FIX_THAT_POSES.length
      setFixFading(true)

      // After fade-out, swap text and fade back in
      setTimeout(() => {
        setPoseIndex(pendingIndex.current)
        setHalloweenGreeting(index === allPhrases.length - 1)
        setFixFading(false)
        setPop(true)
        setTimeout(() => setPop(false), 350)
      }, 300)
    },
    [allPhrases.length]
  )

  const pose = FIX_THAT_POSES[poseIndex]
  const fixLine = halloweenGreeting ? halloweenFixLine : fixLines[poseIndex % fixLines.length]

  return (
    <>
      <TopoFlow ref={topoRef} seed={1} lineCount={8} scale={0.002} speed={0.1} />
      <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
        <div>
          <h1 className="flex min-h-[174px] flex-col items-start justify-center gap-2 leading-tight text-white sm:min-h-[210px]">
            <span className="font-marker flex min-h-[2.5em] w-full items-end text-3xl sm:min-h-0 sm:text-5xl sm:whitespace-nowrap lg:text-6xl">
              <HeroCycler
                phrases={allPhrases}
                artwork={artwork}
                topoFlowRef={topoRef}
                onCycle={handleCycle}
              />
            </span>{' '}
            <span
              className={[
                halloweenGreeting
                  ? 'hero-fix-that font-display inline-block text-[clamp(2.15rem,7.6vw,5.5rem)] leading-none whitespace-nowrap'
                  : 'hero-fix-that font-display inline-block text-6xl leading-none whitespace-nowrap sm:text-7xl lg:text-8xl',
                'transition-opacity duration-300',
                pop && 'hero-fix-that-pop',
                fixFading && 'opacity-0',
              ]
                .filter(Boolean)
                .join(' ')}
              style={
                {
                  color: halloweenGreeting ? '#ffffff' : pose.colour,
                  textShadow: halloweenGreeting ? 'none' : `0 0 20px ${pose.colour}40`,
                  '--highlight-colour': halloweenGreeting ? 'var(--brand-purple)' : pose.highlight,
                  transform: halloweenGreeting
                    ? 'none'
                    : pop
                      ? `translate(${pose.x}px, ${pose.y}px) rotate(${pose.r}deg) scale(1.08)`
                      : `translate(${pose.x}px, ${pose.y}px) rotate(${pose.r}deg) scale(1)`,
                } as React.CSSProperties
              }
              data-text={fixLine}
            >
              {fixLine}
            </span>
          </h1>
          {children}
        </div>
      </div>
    </>
  )
}
